import { useEffect, useMemo, useState } from "react";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { getBook } from "../data/books";
import { getModel } from "../data/models";
import { BookCover } from "../components/BookCover";
import { useApp } from "../context/AppContext";
import { getStripe } from "../lib/stripe";
import { api, ApiError } from "../lib/api";
import { centsToDollars, computeTotals } from "@shared/pricing";
import { US_STATES, shippingAddressSchema, type ShippingAddress } from "@shared/schemas";

type Phase = "form" | "payment";

type ShippingForm = {
  email: string;
  first: string;
  last: string;
  addr1: string;
  addr2: string;
  city: string;
  state: ShippingAddress["state"];
  zip: string;
};

function emptyShipping(): ShippingForm {
  return {
    email: "",
    first: "",
    last: "",
    addr1: "",
    addr2: "",
    city: "",
    state: "NY",
    zip: "",
  };
}

export function CheckoutPage() {
  const { go, cart, clearCart, user } = useApp();
  const totals = useMemo(() => computeTotals(cart), [cart]);

  const [phase, setPhase] = useState<Phase>("form");
  const [form, setForm] = useState<ShippingForm>(() => {
    const base = emptyShipping();
    if (user?.email) base.email = user.email;
    return base;
  });
  const [orderNumber, setOrderNumber] = useState<string | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (cart.length === 0 && phase === "form") go({ name: "home" });
  }, [cart.length, phase, go]);

  const updateField = <K extends keyof ShippingForm>(k: K, v: ShippingForm[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const onContinueToPayment = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitError(null);
    const shipping = shippingAddressSchema.safeParse({
      first: form.first,
      last: form.last,
      addr1: form.addr1,
      addr2: form.addr2 || undefined,
      city: form.city,
      state: form.state,
      zip: form.zip,
      country: "US",
    });
    if (!shipping.success) {
      setSubmitError(shipping.error.issues[0]?.message ?? "Please review your address");
      return;
    }
    setSubmitting(true);
    try {
      const result = await api.createOrder({
        email: form.email,
        items: cart.map(({ bookId, name, gender, modelId, dedication, signature, format }) => ({
          bookId,
          name,
          gender,
          modelId,
          dedication,
          signature,
          format,
        })),
        shipping: shipping.data,
      });
      setOrderNumber(result.orderNumber);
      setClientSecret(result.clientSecret);
      setPhase("payment");
    } catch (err) {
      setSubmitError(err instanceof ApiError ? err.message : "Could not start checkout");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="co-page">
      <div className="wrap">
        <a
          className="co-back"
          href="#"
          onClick={(e) => {
            e.preventDefault();
            if (phase === "payment") setPhase("form");
            else go({ name: "cart" });
          }}
        >
          ← back to {phase === "payment" ? "shipping" : "cart"}
        </a>
        <h1>Checkout.</h1>
        <div className="sub">— SSL/TLS, AES-256 Encrypted, processed by Stripe</div>

        <div className="co-grid">
          <div>
            {phase === "form" && (
              <form onSubmit={onContinueToPayment}>
                <div className="co-section">
                  <h3>
                    <span className="n">1</span> Contact
                  </h3>
                  <div className="field full">
                    <label>Email address</label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => updateField("email", e.target.value)}
                      placeholder="you@home.com"
                      required
                    />
                  </div>
                </div>

                <div className="co-section">
                  <h3>
                    <span className="n">2</span> Shipping address
                  </h3>
                  <div className="field-row">
                    <div className="field">
                      <label>First name</label>
                      <input value={form.first} onChange={(e) => updateField("first", e.target.value)} required />
                    </div>
                    <div className="field">
                      <label>Last name</label>
                      <input value={form.last} onChange={(e) => updateField("last", e.target.value)} required />
                    </div>
                    <div className="field full">
                      <label>Address</label>
                      <input
                        value={form.addr1}
                        onChange={(e) => updateField("addr1", e.target.value)}
                        placeholder="221 Bedford Ave"
                        required
                      />
                    </div>
                    <div className="field full">
                      <label>Apt / Suite (optional)</label>
                      <input value={form.addr2} onChange={(e) => updateField("addr2", e.target.value)} />
                    </div>
                    <div className="field">
                      <label>City</label>
                      <input value={form.city} onChange={(e) => updateField("city", e.target.value)} required />
                    </div>
                    <div className="field">
                      <label>State</label>
                      <select
                        value={form.state}
                        onChange={(e) => updateField("state", e.target.value as ShippingForm["state"])}
                      >
                        {US_STATES.map((s) => (
                          <option key={s}>{s}</option>
                        ))}
                      </select>
                    </div>
                    <div className="field full">
                      <label>ZIP</label>
                      <input
                        value={form.zip}
                        onChange={(e) => updateField("zip", e.target.value)}
                        placeholder="10001"
                        required
                      />
                    </div>
                  </div>
                </div>

                {submitError && (
                  <div className="mono" style={{ color: "var(--rust)", fontSize: 12, marginBottom: 12 }}>
                    {submitError}
                  </div>
                )}

                <button
                  type="submit"
                  className="btn btn-rust big"
                  disabled={submitting || cart.length === 0}
                  style={{ width: "100%", justifyContent: "center" }}
                >
                  {submitting ? "Preparing…" : `Continue to payment — $${centsToDollars(totals.totalCents)}`}{" "}
                  <span className="arr">→</span>
                </button>
              </form>
            )}

            {phase === "payment" && clientSecret && orderNumber && (
              <div className="co-section">
                <h3>
                  <span className="n">3</span> Payment
                </h3>
                <Elements stripe={getStripe()} options={{ clientSecret, appearance: { theme: "stripe" } }}>
                  <PaymentForm
                    orderNumber={orderNumber}
                    totalCents={totals.totalCents}
                    onPaid={(num) => {
                      clearCart();
                      go({ name: "confirm", orderNumber: num });
                    }}
                  />
                </Elements>
                {/* Apple Pay / PaymentRequestButton placeholder — re-enable when ENABLE_PRB is true */}
                {/* <PaymentRequestButtonElement options={{ paymentRequest }} /> */}
              </div>
            )}
          </div>

          <div className="co-summary">
            <h2>
              {cart.length} {cart.length === 1 ? "book" : "books"} in your bag
            </h2>
            {cart.map((item) => {
              const book = getBook(item.bookId);
              const model = getModel(item.modelId);
              if (!book) return null;
              return (
                <div key={item.id} className="co-line">
                  <div className="mini-cover">
                    <BookCover book={book} name={item.name} model={model} />
                  </div>
                  <div>
                    <div className="name">
                      <span>{book.titleSecond}</span>
                    </div>
                    <div className="for">For {item.name}</div>
                  </div>
                  <div className="px">${centsToDollars(item.priceCents)}</div>
                </div>
              );
            })}
            <div className="totals">
              <div className="row">
                <span>Subtotal</span>
                <span>${centsToDollars(totals.subtotalCents)}</span>
              </div>
              <div className="row">
                <span>Shipping</span>
                <span>{totals.shippingCents === 0 ? "Free" : `$${centsToDollars(totals.shippingCents)}`}</span>
              </div>
              <div className="row">
                <span>Tax (est.)</span>
                <span>${centsToDollars(totals.taxCents)}</span>
              </div>
              <div className="row total">
                <span>Total</span>
                <span>${centsToDollars(totals.totalCents)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}

function PaymentForm({
  orderNumber,
  totalCents,
  onPaid,
}: {
  orderNumber: string;
  totalCents: number;
  onPaid: (orderNumber: string) => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onPay = async () => {
    if (!stripe || !elements) return;
    setSubmitting(true);
    setError(null);
    const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/?order=${encodeURIComponent(orderNumber)}`,
      },
      redirect: "if_required",
    });
    setSubmitting(false);
    if (confirmError) {
      setError(confirmError.message ?? "Payment failed");
      return;
    }
    if (paymentIntent && paymentIntent.status === "succeeded") {
      onPaid(orderNumber);
    }
  };

  return (
    <>
      <PaymentElement options={{ layout: "tabs" }} />
      {error && (
        <div className="mono" style={{ color: "var(--rust)", fontSize: 12, marginTop: 12 }}>
          {error}
        </div>
      )}
      <button
        type="button"
        className="btn btn-rust big"
        disabled={submitting || !stripe || !elements}
        onClick={onPay}
        style={{ width: "100%", justifyContent: "center", marginTop: 16 }}
      >
        {submitting ? "Charging…" : `Place order — $${centsToDollars(totalCents)}`} <span className="arr">→</span>
      </button>
    </>
  );
}
